/**
 * SMS Sender Pool Zustand Store
 *
 * Manages the shared pool of outbound SMS sender phone numbers.
 * Outbound SMS picks from this pool with per-Contact stickiness.
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';

export interface SmsSender {
  id: string;
  phoneNumber: string;
  label: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export interface SmsSendersState {
  senders: SmsSender[];
  isLoading: boolean;
  isMutating: boolean;
  error: string | null;

  loadSenders: () => Promise<void>;
  addSender: (phoneNumber: string, label?: string | null) => Promise<boolean>;
  updateSender: (id: string, data: { isActive?: boolean; label?: string | null }) => Promise<boolean>;
  removeSender: (id: string) => Promise<boolean>;
  clearError: () => void;
}

const getInitialState = () => ({
  senders: [] as SmsSender[],
  isLoading: false,
  isMutating: false,
  error: null as string | null,
});

export const useSmsSendersStore = create<SmsSendersState>((set, get) => ({
  ...getInitialState(),

  loadSenders: async () => {
    set({ isLoading: true, error: null });
    try {
      const api = getApiClient();
      const senders = await api.get<SmsSender[]>(API_ENDPOINTS.SUPER_ADMIN.SMS_SENDERS.LIST);
      set({ senders, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load SMS senders',
      });
    }
  },

  addSender: async (phoneNumber, label) => {
    set({ isMutating: true, error: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.SMS_SENDERS.CREATE, { phoneNumber, label });
      set({ isMutating: false });
      await get().loadSenders();
      return true;
    } catch (error) {
      set({
        isMutating: false,
        error: error instanceof Error ? error.message : 'Failed to add SMS sender',
      });
      return false;
    }
  },

  updateSender: async (id, data) => {
    set({ isMutating: true, error: null });
    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.SMS_SENDERS.UPDATE(id), data);
      set({ isMutating: false });
      await get().loadSenders();
      return true;
    } catch (error) {
      set({
        isMutating: false,
        error: error instanceof Error ? error.message : 'Failed to update SMS sender',
      });
      return false;
    }
  },

  removeSender: async (id) => {
    set({ isMutating: true, error: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.SMS_SENDERS.DELETE(id));
      set((state) => ({
        senders: state.senders.filter((s) => s.id !== id),
        isMutating: false,
      }));
      return true;
    } catch (error) {
      set({
        isMutating: false,
        error: error instanceof Error ? error.message : 'Failed to remove SMS sender',
      });
      return false;
    }
  },

  clearError: () => set({ error: null }),
}));
